import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Pencil, Trash2, Copy } from "lucide-react";
import { CrewMember } from "@/types/crew";
import { toast } from "sonner";
import { useTranslation } from 'react-i18next';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

interface CrewMemberCardProps {
  member: CrewMember;
  onEdit?: (member: CrewMember) => void;
  onDelete?: (id: string) => void;
}

export default function CrewMemberCard({ member, onEdit, onDelete }: CrewMemberCardProps) {
  const { t } = useTranslation();

  const handleCopyInfo = () => {
    const info = [
      `${t('crew.member.name')}: ${member.name}`,
      `${t('crew.member.role')}: ${member.role}`,
      member.phone && `${t('crew.member.phone')}: ${member.phone}`,
      member.email && `${t('crew.member.email')}: ${member.email}`,
      member.notes && `${t('crew.member.notes')}: ${member.notes}`
    ].filter(Boolean).join('\n');

    navigator.clipboard.writeText(info).then(() => {
      toast.success(t('crew.member.copy_success'));
    }).catch(() => {
      toast.error(t('crew.member.copy_error'));
    });
  };

  return (
    <Card className="bg-black/40 border-0 text-white">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-lg font-semibold">
          {member.name}
        </CardTitle>
        <div className="flex gap-2">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-white/70 hover:text-white hover:bg-white/10"
            onClick={handleCopyInfo}
            aria-label={t('crew.member.copy_info')}
          >
            <Copy className="h-4 w-4" />
          </Button>
          {onEdit && (
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-white/70 hover:text-white hover:bg-white/10"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onEdit(member);
              }}
              aria-label={t('crew.member.edit')}
            >
              <Pencil className="h-4 w-4" />
            </Button>
          )}
          {onDelete && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-red-400 hover:text-red-300 hover:bg-red-500/10"
                  aria-label={t('crew.member.delete')}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>{t('crew.member.delete_title')}</AlertDialogTitle>
                  <AlertDialogDescription>
                    {t('crew.member.delete_confirm', { name: member.name })}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={() => onDelete(member.id)}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  >
                    {t('common.delete')}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          <p className="text-base text-white/70">
            {member.role}
          </p>
          {member.phone && (
            <p className="text-base">
              <span className="text-white/70">{t('crew.member.phone')}:</span>{" "}
              <a href={`tel:${member.phone}`} className="text-white hover:underline">
                {member.phone}
              </a>
            </p>
          )}
          {member.email && (
            <p className="text-base">
              <span className="text-white/70">{t('crew.member.email')}:</span>{" "}
              <a href={`mailto:${member.email}`} className="text-white hover:underline">
                {member.email}
              </a>
            </p>
          )}
          {member.notes && (
            <p className="text-base">
              <span className="text-white/70">{t('crew.member.notes')}:</span>{" "}
              <span className="text-white">{member.notes}</span>
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}